import { diffLines } from "diff";

export type RowKind = "unchanged" | "removed" | "added" | "modified";
export type Cell = { text: string; lineNo: number } | null;
export type Row = { left: Cell; right: Cell; kind: RowKind };

export type DisplayRow =
  | { kind: "row"; row: Row; index: number }
  | { kind: "collapsed"; key: number; hidden: Row[] };

const splitLines = (value: string): string[] => {
  if (!value) return [];
  const stripped = value.endsWith("\n") ? value.slice(0, -1) : value;
  return stripped.split("\n");
};

const ensureTrailingNewline = (s: string) => (s.endsWith("\n") || s === "" ? s : s + "\n");

export const buildRows = (oldStr: string, newStr: string): Row[] => {
  const chunks = diffLines(ensureTrailingNewline(oldStr), ensureTrailingNewline(newStr));
  const rows: Row[] = [];
  let oldLine = 1;
  let newLine = 1;
  for (let i = 0; i < chunks.length; i++) {
    const cur = chunks[i];
    const next = chunks[i + 1];
    if (cur.removed && next && next.added) {
      const oldLines = splitLines(cur.value);
      const newLines = splitLines(next.value);
      const max = Math.max(oldLines.length, newLines.length);
      for (let j = 0; j < max; j++) {
        const l = oldLines[j];
        const r = newLines[j];
        rows.push({
          left: l !== undefined ? { text: l, lineNo: oldLine++ } : null,
          right: r !== undefined ? { text: r, lineNo: newLine++ } : null,
          kind: l !== undefined && r !== undefined ? "modified" : l !== undefined ? "removed" : "added",
        });
      }
      i++;
    } else if (cur.removed) {
      for (const l of splitLines(cur.value)) {
        rows.push({ left: { text: l, lineNo: oldLine++ }, right: null, kind: "removed" });
      }
    } else if (cur.added) {
      for (const l of splitLines(cur.value)) {
        rows.push({ left: null, right: { text: l, lineNo: newLine++ }, kind: "added" });
      }
    } else {
      for (const l of splitLines(cur.value)) {
        rows.push({
          left: { text: l, lineNo: oldLine++ },
          right: { text: l, lineNo: newLine++ },
          kind: "unchanged",
        });
      }
    }
  }
  return rows;
};

export const countChanges = (rows: Row[]): number => {
  let count = 0;
  let inHunk = false;
  for (const row of rows) {
    if (row.kind === "unchanged") {
      inHunk = false;
    } else if (!inHunk) {
      count++;
      inHunk = true;
    }
  }
  return count;
};

export const collapseRows = (rows: Row[], context: number): DisplayRow[] => {
  const minRun = context * 2 + 1;
  const result: DisplayRow[] = [];
  let i = 0;
  let collapsedKey = 0;
  while (i < rows.length) {
    if (rows[i].kind !== "unchanged") {
      result.push({ kind: "row", row: rows[i], index: i });
      i++;
      continue;
    }
    let j = i;
    while (j < rows.length && rows[j].kind === "unchanged") j++;
    const runLen = j - i;
    const isStart = i === 0;
    const isEnd = j === rows.length;
    const top = isStart ? 0 : context;
    const bot = isEnd ? 0 : context;
    const middle = runLen - top - bot;
    if (runLen < minRun || middle <= 0) {
      for (let k = i; k < j; k++) result.push({ kind: "row", row: rows[k], index: k });
    } else {
      for (let k = i; k < i + top; k++) result.push({ kind: "row", row: rows[k], index: k });
      result.push({ kind: "collapsed", key: collapsedKey++, hidden: rows.slice(i + top, j - bot) });
      for (let k = j - bot; k < j; k++) result.push({ kind: "row", row: rows[k], index: k });
    }
    i = j;
  }
  return result;
};
